<template>
  <Dialog
    id="export-survey-dialog"
    :visible="shouldExportSurvey"
    :closable="!isDownloading"
    :dismissable-mask="!isDownloading"
    :close-on-escape="!isDownloading"
    :breakpoints="{ '1180px': '75vw', '640px': '100vw' }"
    :style="{ width: '50vw' }"
    header="Sizing Survey Export"
    :draggable="false"
    data-cy="export-sizing-dialog"
    @update:visible="emit('update:shouldExportSurvey', false)"
  >
    <h2 class="mt-0" data-cy="file-type-header">File Type</h2>
    <!-- append-to on these dropdowns must be set to self and not body else they do not appear -->
    <Dropdown
      v-model="exportOptions.filetype"
      append-to="self"
      :options="['XLSX', 'CSV']"
      data-cy="file-type-dropdown"
    />
    <h2 data-cy="default-units-header">Select Units</h2>
    <div class="grid" data-cy="units-grid">
      <div
        v-for="depthUnit of depthUnits"
        :key="depthUnit.label"
        class="field-radiobutton p-2"
      >
        <RadioButton
          v-model="selectedDepthUnit"
          name="depthUnit"
          :value="depthUnit"
        />
        <label :for="depthUnit.label" data-cy="depth-unit">{{
          depthUnit.label
        }}</label>
      </div>
    </div>
    <template #footer>
      <div class="text-right my-3">
        <ProgressSpinner
          v-if="isDownloading"
          style="width: 50px; height: 50px"
          strokeWidth="8"
          fill="transparent"
          animationDuration=".5s"
          aria-label="Custom ProgressSpinner"
        />
        <p v-if="!isDownloading">
          <Button
            class="p-button-info"
            data-cy="download-button"
            @click="formatExport"
          >
            Download
          </Button>
        </p>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
/**
 * Xlsx mini build is untyped so ts-ignore is added to satisfy compiler
 * https://github.com/SheetJS/sheetjs/issues/1999
 *
 * Mini build is used because it is 212KB vs 1.2MB
 * for the whole build
 */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
// import XLSX from "xlsx/dist/xlsx.mini.min.js";

import type {
  Sizing,
  DeviationSurvey,
  Scenario
} from "@/composables/forms/formTypes";
import { useSizingUnits } from "@/composables/units/useSizingUnits";
import Dialog from "primevue/dialog";
import type { PropType } from "vue";
import { ref, computed, reactive, watchEffect, nextTick } from "vue";
import ProgressSpinner from "primevue/progressspinner";
import { Feet, Meters } from "@/libs/units/length";
import {
  ThreeDSurveyColumns,
  TwoDSurveyColumns,
  type TargetSurveyExportUnits
} from "@/components/sizing/ExportSurveySections/surveyExportTypes";
import { decimalFormatter } from "@/utils/utils";
// @ts-ignore
import XLSX from "xlsx/dist/xlsx.mini.min.js";
import type { Sheet } from "xlsx";
import { getEnumKeyByValue } from "@/utils/enums";
import { Display_Units_Enum } from "gql/api";
import { orderScenariosBySizingOrder } from "@/utils/scenarioUtils";

const props = defineProps({
  shouldExportSurvey: {
    type: Boolean,
    required: true
  },
  sizings: {
    type: Array as PropType<Sizing[]>,
    required: true,
    validator: (arr: Sizing[]) => Array.isArray(arr) && arr.length > 0
  }
});

const sizing = computed(() => props.sizings[0]);
const scenarios = computed(() =>
  orderScenariosBySizingOrder<Scenario>(sizing.value, sizing.value.scenarios)
);

const deviationSurveysWithTitles = computed(() => {
  return scenarios.value
    .map(scenario => ({
      survey: scenario.well_properties?.r_deviation_survey,
      title: scenario.title
    }))
    .filter(item => item.survey);
});

const deviationSurveys = computed(() => {
  return deviationSurveysWithTitles.value.map(item => item.survey);
});

const emit = defineEmits<{
  (e: "update:shouldExportSurvey", value: boolean): void;
}>();

const isDownloading = ref(false);

const baseSizing = computed(() => {
  return props.sizings[0];
});

const userUnits = useSizingUnits(baseSizing);
const depthUnits = [Feet, Meters];

// TODO: is this really targetLargeLengthUnit?
const exportOptions = reactive({
  filetype: "XLSX" as "XLSX" | "CSV",
  exportUnits: userUnits.targetLargeLengthUnit.value
});

const selectedDepthUnit = ref(Feet.label ? Feet : Meters);

async function formatExport(): Promise<void> {
  if (isDownloading.value) return;
  isDownloading.value = true;

  try {
    await nextTick();
    await new Promise(r => setTimeout(r, 0)); // eslint-disable-line promise/param-names

    const targetUnits = { ...exportOptions } as TargetSurveyExportUnits;
    const wb = XLSX.utils.book_new();

    if (exportOptions.filetype === "CSV") {
      const allData: unknown[][] = [];
      let isFirstSurvey = true;
      for (const item of deviationSurveysWithTitles.value) {
        if (item.survey) {
          const { sheetData } = await getSurveySheetData(
            item.survey,
            item.title,
            targetUnits,
            baseSizing.value
          );
          if (isFirstSurvey) {
            allData.push(...sheetData);
            isFirstSurvey = false;
          } else {
            // Add scenario title and headers for subsequent surveys
            allData.push([]);
            allData.push(["", "Scenario Title", "", item.title]);
            allData.push([]);
            allData.push(sheetData.find(r => r[0] === "Survey Rows") || []); // get header
            allData.push(
              ...sheetData.slice(
                sheetData.findIndex(r => r[0] === "Survey Rows") + 1
              )
            );
          }
        }
      }
      const singleSheet = XLSX.utils.aoa_to_sheet(allData);
      XLSX.utils.book_append_sheet(wb, singleSheet, "Surveys");
    } else {
      for (const item of deviationSurveysWithTitles.value) {
        if (item.survey) {
          await processSurvey(
            item.survey,
            item.title,
            wb,
            targetUnits,
            baseSizing.value
          );
        }
      }
    }

    const fileStem =
      (deviationSurveys.value?.length > 1
        ? "Surveys_Export"
        : baseSizing.value.title) || "Sizing";

    const stamp = new Date().toLocaleDateString();
    XLSX.writeFile(
      wb,
      `${fileStem.substring(0, 50)} ${stamp}.${exportOptions.filetype.toLowerCase()}`
    );
  } catch (err) {
    console.error("Export failed:", err);
  } finally {
    isDownloading.value = false;
    emit("update:shouldExportSurvey", false);
  }
}

async function getSurveySheetData(
  survey: DeviationSurvey,
  title: string | null | undefined,
  targetUnits: TargetSurveyExportUnits,
  sizing: Sizing
) {
  const sizingInfo = [
    ["Sizing Info", "Sizing Name", "", sizing?.title],
    ["", "Export Date", "", new Date().toLocaleDateString("en-us")],
    [
      "",
      "User Name / ID",
      "",
      `${sizing?.author?.display_name} / ${sizing?.author?.email}`
    ],
    ["", "Well Name", "", sizing?.well?.well_name],
    ["", "Customer", "", sizing?.customer?.customer_name],
    ["", "Notes", "", sizing?.notes],
    ["", "Scenario Title", "", title],
    [
      "",
      "Unit System",
      "",
      getEnumKeyByValue(Display_Units_Enum, sizing?.default_display_units)
    ]
  ];

  const is3d =
    survey?.directional_data_set === "three_d_md_inclination_azimuth";

  const baseHeaders = is3d
    ? [
        ThreeDSurveyColumns.MeasuredDepth,
        ThreeDSurveyColumns.Inclination,
        ThreeDSurveyColumns.Azimuth,
        ThreeDSurveyColumns.TrueVerticalDepth,
        ThreeDSurveyColumns.DoglegSeverity
      ]
    : [
        TwoDSurveyColumns.MeasuredDepth,
        TwoDSurveyColumns.Inclination,
        TwoDSurveyColumns.TrueVerticalDepth
      ];

  const colsThatNeedUnits = [
    ThreeDSurveyColumns.MeasuredDepth,
    ThreeDSurveyColumns.TrueVerticalDepth,
    TwoDSurveyColumns.MeasuredDepth,
    TwoDSurveyColumns.TrueVerticalDepth
  ];

  const headers = baseHeaders.map(header => {
    if (colsThatNeedUnits.includes(header)) {
      return `${header} (${selectedDepthUnit.value.label})`;
    }
    if (header === ThreeDSurveyColumns.DoglegSeverity) {
      const formattedUnit = decimalFormatter(
        selectedDepthUnit.value.toThisUnit(100),
        false,
        2
      );
      return `${header} (Degrees/${formattedUnit}${selectedDepthUnit.value.shortLabel})`;
    }
    return header;
  });

  // The source data from survey.entry_rows always has 5 columns in this order.
  const sourceColumns = [
    ThreeDSurveyColumns.MeasuredDepth,
    ThreeDSurveyColumns.Inclination,
    ThreeDSurveyColumns.Azimuth,
    ThreeDSurveyColumns.TrueVerticalDepth,
    ThreeDSurveyColumns.DoglegSeverity
  ];

  type SurveyCell = number | string | null | undefined;
  type SurveyRow = SurveyCell[];
  const entryRows = (survey?.entry_rows ?? []) as SurveyRow[];

  const dataRows = entryRows.map((row: SurveyRow) => {
    const convertedRow = baseHeaders.map(baseHeader => {
      // Find the index of the column in the original source data.
      const sourceIndex = sourceColumns.indexOf(
        baseHeader as ThreeDSurveyColumns
      );
      const val = row[sourceIndex];
      const needsConversion = colsThatNeedUnits.includes(baseHeader);

      if (needsConversion && typeof val === "number" && !isNaN(val)) {
        interface DepthUnit {
          toThisUnit: (val: number) => number;
          label: string;
        }
        const convertedValue: number = (
          selectedDepthUnit.value as DepthUnit
        ).toThisUnit(val);
        return decimalFormatter(convertedValue, false, 2);
      }
      if (
        (baseHeader === ThreeDSurveyColumns.DoglegSeverity ||
          baseHeader === ThreeDSurveyColumns.Inclination) &&
        typeof val === "number" &&
        !isNaN(val)
      ) {
        return decimalFormatter(val, false, 2);
      }
      if (val === null || val === undefined) {
        return "";
      }
      return String(val);
    });
    return ["", ...convertedRow];
  });

  const sheetData = [
    ...sizingInfo,
    [],
    ["Survey Rows", ...headers],
    ...dataRows
  ];

  return { sheetData, sizingInfo };
}

async function processSurvey(
  survey: DeviationSurvey,
  title: string | null | undefined,
  workbook: XLSX.WorkBook,
  targetUnits: TargetSurveyExportUnits,
  sizing: Sizing
): Promise<void> {
  const { sheetData, sizingInfo } = await getSurveySheetData(
    survey,
    title,
    targetUnits,
    sizing
  );

  const sheet: Sheet = XLSX.utils.aoa_to_sheet(sheetData, {
    skipHeader: true
  });

  // Apply cell styles
  for (const address in sheet) {
    if (address[0] === "!") continue;
    const cell = sheet[address];
    if (!cell.s) cell.s = {};

    // Default left alignment
    cell.s.alignment = { horizontal: "left" };
  }

  // XLSX cell magic
  const sizingInfoSectionRowCount = sizingInfo.length + 1; // +1 for spacer
  const surveyHeaderRowIndex = sizingInfoSectionRowCount;

  sheet["!merges"] = [
    // Merge "Sizing Info" section (rows 1-9)
    { s: { r: 0, c: 0 }, e: { r: sizingInfoSectionRowCount - 1, c: 0 } },
    // Merge "Survey Rows" section (row 10 to end)
    {
      s: { r: surveyHeaderRowIndex, c: 0 },
      e: { r: sheetData.length - 1, c: 0 }
    }
  ];

  if (sheetData.length > 9 && sheetData[9].length > 0) {
    const surveyHeaderRow = sheetData[surveyHeaderRowIndex];
    const colWidths = surveyHeaderRow.map((_, i) => {
      const maxLength = Math.max(
        ...sheetData.map(row => (row[i] ? String(row[i]).length : 0))
      );
      return { wch: Math.max(maxLength, 10) };
    });
    sheet["!cols"] = colWidths;
  }

  // Set row heights (in pixels)
  sheet["!rows"] = sheetData.map(() => ({ hpx: 20 }));

  const sheetName = buildSheetName(title ?? "Sheet", workbook.SheetNames);
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName);

  await nextTick();
}

function buildSheetName(rawTitle: string, existing: string[]): string {
  const MAX = 31;

  const clean = (rawTitle || "Sheet")
    .replace(/[\\/:*?\[\]]/g, "-") // eslint-disable-line no-useless-escape
    .replace(/\s+/g, " ")
    .trim();

  const base = clean.substring(0, MAX);
  let name = base;
  let i = 1;

  while (existing.some(n => n.toLowerCase() === name.toLowerCase())) {
    const suffix = `(${i})`;
    const room = MAX - suffix.length;
    name = `${base.substring(0, room)}${suffix}`;
    i += 1;
  }
  return name;
}

watchEffect(() => {
  if (props.shouldExportSurvey) {
    Object.assign(exportOptions, {
      filetype: "XLSX",
      exportUnits: userUnits.targetLargeLengthUnit.value
    });
  }
});
</script>
